import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Typography, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Checkbox, Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import BlockIcon from "@material-ui/icons/Block";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import copyToClipboard from "../../utils/copyToClipboard";

const ALL_SCOPES = [
  "clients:read", "clients:write",
  "contacts:read",
  "tickets:read",
  "deals:read"
];

const fmt = (v) => { try { return v ? new Date(v).toLocaleString() : "—"; } catch (e) { return "—"; } };

const useStyles = makeStyles((theme) => ({
  code: {
    fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  scopes: { display: "flex", gap: 4, flexWrap: "wrap" },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tokenRow: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
}));

const ApiTokens = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api-tokens");
      setTokens(Array.isArray(data) ? data : []);
    } catch (e) { toast.error("Erro ao carregar tokens"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!name) return toast.warn("Informe um nome");
    try {
      const { data } = await api.post("/api-tokens", {
        name, scopes, expiresAt: expiresAt || null
      });
      setCreated(data);
      setOpen(false);
      setName(""); setScopes([]); setExpiresAt("");
      load();
    } catch (e) { toast.error("Erro ao criar token"); }
  };

  const onRevoke = async (id) => {
    if (!(await confirm({ title: "Revogar este token?", message: "Qualquer integração que o utilize para de autenticar na hora." }))) return;
    try { await api.post(`/api-tokens/${id}/revoke`); load(); } catch (e) { toast.error("Erro"); }
  };
  const onDelete = async (id) => {
    if (!(await confirm({ title: "Excluir o token definitivamente?", message: "O registro sai da lista e o histórico de uso é perdido." }))) return;
    try { await api.delete(`/api-tokens/${id}`); load(); } catch (e) { toast.error("Erro"); }
  };
  const copy = async (v) => {
    if (await copyToClipboard(v)) toast.success("Copiado!");
    else toast.error("Não foi possível copiar. Selecione e copie à mão.");
  };

  const toggleScope = (s) => {
    setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const columns = [
    { key: "name", label: "Nome", render: t => <Typography variant="subtitle2">{t.name}</Typography> },
    { key: "prefix", label: "Prefixo", render: t => <code className={classes.code}>{t.prefix}</code> },
    {
      key: "scopes",
      label: "Escopos",
      render: t => {
        const list = String(t.scopes || "").split(",").map(s => s.trim()).filter(Boolean);
        // Sem escopo declarado o token vale para tudo. Isso precisa ficar
        // visível na lista, não escondido atrás de um asterisco.
        if (!list.length) return <StatusChip tone="warning" label="acesso total" />;
        return (
          <div className={classes.scopes}>
            {list.map(s => <StatusChip key={s} tone="neutral" label={s} />)}
          </div>
        );
      },
    },
    { key: "lastUsedAt", label: "Último uso", render: t => <span className={classes.date}>{fmt(t.lastUsedAt)}</span> },
    { key: "expiresAt", label: "Expira em", render: t => <span className={classes.date}>{fmt(t.expiresAt)}</span> },
    {
      key: "status",
      label: "Situação",
      render: t => (
        <StatusChip
          dot
          tone={t.revokedAt ? "danger" : "success"}
          label={t.revokedAt ? "Revogado" : "Ativo"}
        />
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: t => (
        <>
          {!t.revokedAt && (
            <Tooltip title="Revogar">
              <IconButton size="small" onClick={() => onRevoke(t.id)}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => onDelete(t.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Tokens de API (v1)"
        count={tokens.length}
        subtitle="Credenciais para integrações externas consumirem /api/v1. O token completo só aparece na criação."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Novo token
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={tokens}
        loading={loading}
        emptyIcon={<VpnKeyIcon />}
        emptyTitle="Nenhum token gerado"
        emptyDescription="Gere um token para que sistemas externos consultem clientes, contatos, tickets ou negócios pela API."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Novo token
          </Button>
        }
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo token de API</DialogTitle>
        <DialogContent>
          <TextField label="Nome" fullWidth value={name} onChange={e => setName(e.target.value)} margin="dense"
            helperText="Quem vai usar este token — é o nome que identifica a integração depois." />
          <TextField label="Expira em (ISO, opcional)" fullWidth value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)} margin="dense" placeholder="2026-12-31T23:59:59Z" />
          <Typography variant="subtitle2" style={{ marginTop: 12 }}>Escopos</Typography>
          <Typography variant="caption" color="textSecondary">
            Nenhum marcado = acesso a tudo. Marque só o que a integração precisa.
          </Typography>
          <div>
            {ALL_SCOPES.map(s => (
              <FormControlLabel key={s}
                control={<Checkbox checked={scopes.includes(s)} onChange={() => toggleScope(s)} />}
                label={<span className={classes.code}>{s}</span>}
              />
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={onCreate}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!created} onClose={() => setCreated(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Token gerado</DialogTitle>
        <DialogContent>
          <Typography color="error" gutterBottom>
            <strong>Copie agora.</strong> Este é o único momento em que o token completo será exibido.
          </Typography>
          <Box className={classes.tokenRow}>
            <TextField fullWidth value={created?.token || ""} InputProps={{ readOnly: true }} />
            <Tooltip title="Copiar">
              <IconButton onClick={() => copy(created?.token)}><FileCopyIcon /></IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption">
            Use no header: <code className={classes.code}>Authorization: Bearer &lt;token&gt;</code>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreated(null)} variant="contained" color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ApiTokens;
